/**
 * The frame every signed-in screen renders inside: icon rail on the left,
 * command bar across the top, AI panel on the right, content in the middle.
 *
 * The shell stays quiet on purpose. The rings and the due-date strip carry the
 * personality; chrome that competes with them would make both harder to read.
 */
import { Suspense } from 'react'
import { Outlet, ScrollRestoration } from 'react-router-dom'
import { TooltipProvider } from '@/components/ui/tooltip'
import { Skeleton } from '@/components/ui/skeleton'
import { ScopeProvider } from './scope'
import { AiPanelProvider } from './ai-panel'
import { AiPanelContainer } from './AiPanelContainer'
import { BottomBar } from './BottomBar'
import { CommandBar } from './CommandBar'
import { IconRail } from './IconRail'

export function AppShell() {
  return (
    <TooltipProvider delayDuration={300}>
      <ScopeProvider>
      <AiPanelProvider>
        <div className="bg-ink text-foreground min-h-dvh overflow-x-clip">
          {/* First stop for a keyboard: ten rail links before the content is a
              long way to travel on every navigation. */}
          <a
            href="#main"
            className="bg-panel text-foreground border-hairline focus-visible:outline-ring sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:rounded-md focus:border focus:px-3 focus:py-2 focus:text-sm focus-visible:outline-2 focus-visible:outline-offset-2"
          >
            Skip to content
          </a>

          <IconRail />

          <div className="md:pl-14">
            <CommandBar />

            <div className="flex items-start">
              <main
                id="main"
                // Focusable only as the skip link's target — without this Safari
                // moves the scroll but leaves focus in the rail.
                tabIndex={-1}
                // Bottom padding clears the mobile bar; `min-w-0` stops a wide
                // table from pushing the whole layout sideways.
                // Bottom padding clears the mobile bar; `min-w-0` stops a wide
                // table from pushing the whole layout sideways. The desktop
                // padding is generous on purpose — at 2rem the last row sat
                // flush against the viewport edge and read as "cannot scroll
                // further" even when the page had ended.
                className="min-w-0 flex-1 px-3 pt-5 pb-28 sm:px-5 md:pb-16"
              >
                <Suspense fallback={<ScreenSkeleton />}>
                  <Outlet />
                </Suspense>
              </main>

              <AiPanelContainer />
            </div>
          </div>

          <BottomBar />

          {/*
            * Without this the window keeps its scroll offset across a route
            * change — an SPA navigation is not a document navigation, so the
            * browser has no reason to move — and you land on Transactions
            * already scrolled to wherever you were on Dashboard. It also
            * restores the offset on back/forward, which a plain scroll-to-top
            * effect would throw away.
            */}
          <ScrollRestoration />
        </div>
      </AiPanelProvider>
      </ScopeProvider>
    </TooltipProvider>
  )
}

/** Shown while a lazily-loaded screen arrives. Shaped like a page, not a spinner. */
function ScreenSkeleton() {
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-7 w-56" />
      </div>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }, (_, index) => (
          <Skeleton key={index} className="h-28 w-full" />
        ))}
      </div>
      <Skeleton className="h-64 w-full" />
    </div>
  )
}
